import { type ItemDetails } from '@1debit/analytics';
import * as Dialog from '@radix-ui/react-dialog';
import { forwardRef, type ReactNode } from 'react';

import { FullPageModalContent } from './FullPageModalContent';
import { ModalProvider } from './ModalContext';
import {
	ModalClose,
	type ModalCloseProps,
	ModalFooter,
	type ModalFooterProps,
	ModalTitle,
	type ModalTitleProps,
	ModalTrigger,
	type ModalTriggerProps,
} from './subcomponents';

import { type AnalyticsId } from '../../client/AnalyticsProvider/AnalyticsProvider';
import { useModalOpenChangeAnalytics } from '../../client/AnalyticsProvider/hooks/useModalOpenChangeAnalytics';
import { classNames } from '../../server/utils/classNames';

export type FullPageModalTriggerProps = ModalTriggerProps;
export type FullPageModalCloseProps = ModalCloseProps;
export type FullPageModalFooterProps = ModalFooterProps;
export type FullPageModalTitleProps = ModalTitleProps;
export type { FullPageModalContentProps } from './FullPageModalContent';

export type FullPageModalBodyProps = {
	children: ReactNode;
	className?: string;
};
export const FullPageModalBody = forwardRef<HTMLDivElement, FullPageModalBodyProps>(({ children, className }, ref) => {
	return (
		<div
			className={classNames(
				'px-6 flex w-full text-content-primary flex-col grow sm:grow-0 overflow-y-auto sm:overflow-y-visible',
				className,
			)}
			ref={ref}>
			{children}
		</div>
	);
});

export type FullPageModalProps = Dialog.DialogProps & {
	analyticsId: AnalyticsId;
	itemDetails?: ItemDetails;
};

export const FullPageModal = ({ analyticsId, onOpenChange, itemDetails, ...props }: FullPageModalProps) => {
	const { handleModalOpenChangeAnalytics } = useModalOpenChangeAnalytics({
		analyticsId,
		defaultOpen: props.defaultOpen,
		onOpenChange,
		open: props.open,
		itemDetails,
	});

	return (
		<ModalProvider analyticsId={analyticsId}>
			<Dialog.Root onOpenChange={handleModalOpenChangeAnalytics} {...props} />
		</ModalProvider>
	);
};

FullPageModal.Trigger = ModalTrigger;
FullPageModal.Body = FullPageModalBody;
FullPageModal.Content = FullPageModalContent;
FullPageModal.Close = ModalClose;
FullPageModal.Footer = ModalFooter;
FullPageModal.Title = ModalTitle;
